
import React, { useEffect, useState } from "react";
import "./TablesQR.css";
import { getOperationsCsrf } from "../operations/operationsApi";
import { QRCodeCanvas } from "qrcode.react";

const base = (id) => `/api/operations/${id}/tables/`;

async function api(url, options = {}) {
  const response = await fetch(url, {
    credentials: "include",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(
      data.error ||
      data.detail ||
      `Request failed (${response.status})`
    );
  }

  return data;
}

export default function TablesQR({ restaurantId }) {
  const [tables, setTables] = useState([]);
  const [tableLimit, setTableLimit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newTable, setNewTable] = useState({ table_number: "", capacity: "4" });
  const [error, setError] = useState("");

  const [editForm, setEditForm] = useState({
    table_number: "",
    capacity: "4",
  });

  useEffect(() => {
    let mounted = true;

    async function fetchTables() {
      setLoading(true);
      setError("");

      try {
        const result = await api(base(restaurantId));

        if (mounted) {
          setTables(result.tables || []);
          setTableLimit(result.table_limit);
        }
      } catch (err) {
        if (mounted) {
          setError(err.message);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    fetchTables();

    return () => {
      mounted = false;
    };
  }, [restaurantId]);

  async function refreshTables() {
    const result = await api(base(restaurantId));
    setTables(result.tables || []);
    setTableLimit(result.table_limit);
  }

  async function addTable(event) {
    event.preventDefault();
    const number = newTable.table_number.trim();
    const capacity = Number(newTable.capacity);
    if (!number || number.length > 30) { setError("Enter a table number (1–30 characters)."); return; }
    if (!Number.isInteger(capacity) || capacity < 1 || capacity > 1000) {
      setError("Capacity must be between 1 and 1000."); return;
    }
    setAdding(true);
    setError("");
    try {
      const csrf = await getOperationsCsrf();
      await api(base(restaurantId), {
        method: "POST",
        headers: { "X-CSRFToken": csrf.csrfToken },
        body: JSON.stringify({ table_number: number, capacity }),
      });
      await refreshTables();
      setShowAdd(false);
      setNewTable({ table_number: "", capacity: "4" });
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  }

  function downloadQr(table) {
    const canvas = document.getElementById(`tq-qr-${table.id}`);
    if (!canvas) return;
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = `table-${table.table_number}-qr.png`;
    link.click();
  }

  function startEdit(table) {
    setEditingId(table.id);

    setEditForm({
      table_number: table.table_number,
      capacity: String(table.capacity),
    });

    setError("");
  }

  function cancelEdit() {
    setEditingId(null);
    setError("");
  }

  async function updateTable(table, changes) {
    setBusyId(table.id);
    setError("");

    try {
      const csrf = await getOperationsCsrf();
      await api(`${base(restaurantId)}${table.id}/`, {
        method: "PATCH",
        headers: {
          "X-CSRFToken": csrf.csrfToken,
        },
        body: JSON.stringify(changes),
      });

      await refreshTables();

      setEditingId(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  }

  async function saveEdit(event, table) {
    event.preventDefault();

    const tableNumber = editForm.table_number.trim();
    const capacity = Number(editForm.capacity);

    if (!tableNumber || tableNumber.length > 30) {
      setError("Enter a valid table number.");
      return;
    }

    if (
      !Number.isInteger(capacity) ||
      capacity < 1 ||
      capacity > 1000
    ) {
      setError("Capacity must be between 1 and 1000.");
      return;
    }

    await updateTable(table, {
      table_number: tableNumber,
      capacity,
    });
  }

  async function toggleStatus(table) {
    const action = table.is_active
      ? "deactivate"
      : "activate";

    if (
      !window.confirm(
        `Are you sure you want to ${action} Table ${table.table_number}?`
      )
    ) {
      return;
    }

    await updateTable(table, {
      is_active: !table.is_active,
    });
  }

  return (
    <section className="tq-page">
      <div className="tq-heading">
        <h2>Table Management</h2>
        <p>Add and edit tables within your FoodHorizon allocation.</p>
        {tableLimit !== null && <p>Tables used: {tables.length} / {tableLimit}</p>}
        {tableLimit !== null && tables.length >= tableLimit ? (
          <p className="tq-limit">You have reached your allocated table limit of {tableLimit}. Contact your FoodHorizon service provider to add more tables. Additional monthly charges may apply.</p>
        ) : (
          <button type="button" className="tq-add-button" onClick={() => setShowAdd((v) => !v)}>
            {showAdd ? "Cancel adding" : "+ Add Table"}
          </button>
        )}
      </div>

      {error && (
        <p className="tq-error" role="alert">
          {error}
        </p>
      )}

      {showAdd && !loading && (tableLimit === null || tables.length < tableLimit) && (
        <form className="tq-add-form tq-edit-form" onSubmit={addTable}>
          <h3>Add new table</h3>
          <label>Table Number
            <input type="text" required maxLength={30} value={newTable.table_number}
              onChange={(e) => setNewTable((v) => ({ ...v, table_number: e.target.value }))} />
          </label>
          <label>Seating Capacity
            <input type="number" required min="1" max="1000" value={newTable.capacity}
              onChange={(e) => setNewTable((v) => ({ ...v, capacity: e.target.value }))} />
          </label>
          <button type="submit" className="tq-add-button" disabled={adding || busyId !== null}>
            {adding ? "Creating..." : "Create Table"}
          </button>
        </form>
      )}

      {loading ? (
        <p>Loading tables...</p>
      ) : tables.length === 0 ? (
        <div className="tq-empty">
          <p>No tables are available.</p>
        </div>
      ) : (
        <div className="tq-grid">
          {tables.map((table) => (
            <article className="tq-card" key={table.id}>
              {editingId === table.id ? (
                <form
                  className="tq-edit-form"
                  onSubmit={(event) =>
                    saveEdit(event, table)
                  }
                >
                  <label>
                    Table Number
                    <input
                      type="text"
                      maxLength={30}
                      required
                      value={editForm.table_number}
                      onChange={(event) =>
                        setEditForm((previous) => ({
                          ...previous,
                          table_number: event.target.value,
                        }))
                      }
                    />
                  </label>

                  <label>
                    Seating Capacity
                    <input
                      type="number"
                      min="1"
                      max="1000"
                      required
                      value={editForm.capacity}
                      onChange={(event) =>
                        setEditForm((previous) => ({
                          ...previous,
                          capacity: event.target.value,
                        }))
                      }
                    />
                  </label>

                  <div className="tq-actions">
                    <button
                      type="submit"
                      disabled={busyId !== null}
                      className="tq-save"
                    >
                      {busyId === table.id
                        ? "Saving..."
                        : "Save"}
                    </button>

                    <button
                      type="button"
                      disabled={busyId !== null}
                      className="tq-cancel"
                      onClick={cancelEdit}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  <h3>Table {table.table_number}</h3>

                  <p>
                    Seating Capacity: {table.capacity}
                  </p>

                  <p>
                    Status:{" "}
                    <span
                      className={
                        table.is_active
                          ? "tq-status-active"
                          : "tq-status-inactive"
                      }
                    >
                      {table.is_active
                        ? "Active"
                        : "Inactive"}
                    </span>
                  </p>

                  <div className="tq-qr-preview">
                    <QRCodeCanvas id={`tq-qr-${table.id}`}
                      value={`${window.location.origin}/${encodeURIComponent(restaurantId)}/table/${table.id}/`}
                      size={128} marginSize={2} />
                    <button type="button" className="tq-qr-download" onClick={() => downloadQr(table)}>
                      Download QR
                    </button>
                  </div>
                  <div className="tq-actions">
                    <button
                      type="button"
                      className="tq-edit"
                      disabled={busyId !== null}
                      onClick={() => startEdit(table)}
                    >
                      Edit
                    </button>

                    <button
                      type="button"
                      className={
                        table.is_active
                          ? "tq-deactivate"
                          : "tq-activate"
                      }
                      disabled={busyId !== null}
                      onClick={() => toggleStatus(table)}
                    >
                      {busyId === table.id
                        ? "Updating..."
                        : table.is_active
                        ? "Deactivate"
                        : "Activate"}
                    </button>
                  </div>
                </>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}